import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { MatchData } from '@/interfaces/liveGamesType';
import { DashboardState, GroupedMatches, League, TabsValues, TabsType } from '../types/index';
import {filterMatches, getMatchesByTab, groupMatchesByLeague} from "../utils/index"
import { useAuth } from '@/context/AuthContext';
import { useTheme, useMediaQuery } from '@mui/material';
import { fetchGetHistMatches } from '@/hooks/fetchHooks/fetchGetHistMatches';
import { fetchGetLiveMatches } from '@/hooks/fetchHooks/fetchGetLiveMatches';

// Chave para armazenar favoritos no localStorage
const FAVORITES_STORAGE_KEY = 'sportPrecision_favorites';

// Função para carregar favoritos do localStorage
const loadFavoritesFromStorage = (): string[] => {
  try {
    const storedFavorites = localStorage.getItem(FAVORITES_STORAGE_KEY);
    return storedFavorites ? JSON.parse(storedFavorites) : [];
  } catch (error) {
    console.error('Erro ao carregar favoritos do localStorage:', error);
    return [];
  }
};

// Função para salvar favoritos no localStorage
const saveFavoritesToStorage = (favorites: string[]): void => {
  try {
    localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(favorites));
  } catch (error) {
    console.error('Erro ao salvar favoritos no localStorage:', error);
  }
};

export const useMatches = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const lastDateRef = useRef<Date>(new Date());
  const isLoadingRef = useRef<boolean>(false);
  
  // Estado do componente
  const [state, setState] = useState<DashboardState>({
    date: new Date(),
    tabValue: TabsValues.ALL,
    leagues: [],
    liveMatches: [],
    finishedMatches: [],
    selectedMatch: null,
    favorites: loadFavoritesFromStorage(), // Carregar favoritos do localStorage
    searchQuery: '',
    openDetailsMobile: false,
    selectedLeagueId: null
  });

  const { isAuthenticated } = useAuth()

  // Extraindo valores do estado para facilitar o acesso
  const {
    date,
    tabValue,
    liveMatches,
    finishedMatches,
    selectedMatch,
    favorites,
    searchQuery,
    openDetailsMobile,
    selectedLeagueId
  } = state;

  // Salvar favoritos no localStorage quando forem alterados
  useEffect(() => {
    saveFavoritesToStorage(favorites);
  }, [favorites]);

  const getLiveMatches = fetchGetLiveMatches({
    meta: {
      errorMessage: "Ocorreu um erro ao tentar buscar as partidas ao vivo",
    },
  }, isAuthenticated);
  const historicalMatches = fetchGetHistMatches(date, {
    meta: {
      errorMessage: "Ocorreu um erro ao tentar buscar as partidas passadas",
    },
  }, isAuthenticated);

  // Atualizar a referência de loading diretamente sem useEffect
  isLoadingRef.current = getLiveMatches.isLoading || historicalMatches.isLoading;

  // Atualizar a data de referência quando a data do estado mudar
  useEffect(() => {
    if (date.getTime() !== lastDateRef.current.getTime()) {
      lastDateRef.current = date;
    }
  }, [date]);

  // Carregar dados iniciais
  useEffect(() => {
    // Evitar atualizações de estado durante carregamento
    if (isLoadingRef.current) return;

    // Só atualizar o estado se os dados mudaram ou se a data mudou
    const shouldUpdateLiveMatches = getLiveMatches.data !== state.liveMatches;
    const shouldUpdateHistMatches = historicalMatches.data !== state.finishedMatches;
    
    if (shouldUpdateLiveMatches || shouldUpdateHistMatches) {
      setState(prev => {
        let newSelectedMatch = prev.selectedMatch;
        if (!newSelectedMatch && getLiveMatches.data.length > 0) {
          newSelectedMatch = getLiveMatches.data[0];
        } else if (!newSelectedMatch && historicalMatches.data.length > 0) {
          newSelectedMatch = historicalMatches.data[0];
        }

        return {
          ...prev,
          liveMatches: getLiveMatches.data,
          finishedMatches: historicalMatches.data,
          selectedMatch: newSelectedMatch,
          selectedLeagueId: shouldUpdateHistMatches ? null : prev.selectedLeagueId
        };
      });
    }
  }, [getLiveMatches.data, historicalMatches.data]);

  // Memoized handler for tab change
  const handleTabChange = useCallback((_: React.SyntheticEvent, newValue: number) => {
    setState(prev => ({ ...prev, tabValue: newValue as TabsType, selectedLeagueId: null }));
  }, []);

  // Memoized handler for date change
  const changeDate = useCallback((direction: 'prev' | 'next') => {
    // Se estiver carregando, não permitir mudança de data
    if (isLoadingRef.current) return;
    
    setState(prev => {
      const newDate = new Date(prev.date);
      if (direction === 'prev') {
        newDate.setDate(newDate.getDate() - 1);
      } else {
        newDate.setDate(newDate.getDate() + 1);
      }
      return { ...prev, date: newDate, selectedLeagueId: null };
    });
  }, []);

  // Memoized function for selecting specific date
  const selectSpecificDate = useCallback((newDate: Date) => {
    // Se estiver carregando, não permitir mudança de data
    if (isLoadingRef.current) return;
    
    setState(prev => {
      // Evitar rederização desnecessária se a data for a mesma
      if (newDate.toDateString() === prev.date.toDateString()) return prev;
      return { ...prev, date: newDate, selectedLeagueId: null };
    });
  }, []);

  // Memoized handler for search
  const handleSearch = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setState(prev => ({ ...prev, searchQuery: event.target.value, selectedLeagueId: null }));
  }, []);

  // Memoized handler for favorite toggle
  const toggleFavorite = useCallback((id: string) => {
    setState(prev => {
      const newFavorites = prev.favorites.includes(id)
        ? prev.favorites.filter(fav => fav !== id)
        : [...prev.favorites, id];
      
      return { ...prev, favorites: newFavorites };
    });
  }, []);

  // Memoized handler for match selection
  const selectMatch = useCallback((match: MatchData) => {
    setState(prev => ({ ...prev, selectedMatch: match, openDetailsMobile: isMobile }));
  }, [isMobile]);

  // Memoized function to close mobile details
  const closeMobileDetails = useCallback(() => {
    setState(prev => ({ ...prev, openDetailsMobile: false }));
  }, []);

  // Memoized handler for league selection
  const selectLeague = useCallback((leagueId: string) => {
    setState(prev => {
      // Se a liga já estiver selecionada, desmarca a seleção
      if (prev.selectedLeagueId === leagueId) {
        return { ...prev, selectedLeagueId: null };
      }
      // Caso contrário, seleciona a liga
      return { ...prev, selectedLeagueId: leagueId };
    });
  }, []);

  // Memoize filtered matches calculation
  const filteredMatches = useMemo(() => {
    // Obter partidas de acordo com a aba selecionada
    let matches = getMatchesByTab(tabValue, liveMatches, finishedMatches, favorites);

    // Aplicar filtro de pesquisa e liga selecionada
    return filterMatches(matches, searchQuery, selectedLeagueId);
  }, [tabValue, liveMatches, finishedMatches, favorites, searchQuery, selectedLeagueId]);

  // Memoize grouped matches calculation
  const groupedMatches = useMemo(() => {
    return groupMatchesByLeague(filteredMatches);
  }, [filteredMatches]);

  // Verificar se algum dos fetchs está carregando
  const loading = getLiveMatches.isLoading || historicalMatches.isLoading;

  return {
    state,
    loading,
    filteredMatches,
    groupedMatches,
    handleTabChange,
    changeDate,
    selectSpecificDate,
    handleSearch,
    toggleFavorite,
    selectMatch,
    isMobile,
    openDetailsMobile,
    closeMobileDetails,
    selectLeague
  };
};